import React, { useState, useEffect, useRef } from 'react';
import { useGetJobCardStateQuery } from '../api/apiSlice';

// Connection quality thresholds (in milliseconds)
const CONNECTION_THRESHOLDS = {
  STRONG: 1000,    // < 1 second
  WEAK: 5000,      // 1-5 seconds
  VERY_WEAK: 5000  // > 5 seconds
};

// Connection quality levels
export const CONNECTION_QUALITY = {
  STRONG: 'strong',
  WEAK: 'weak', 
  VERY_WEAK: 'very_weak',
  OFFLINE: 'offline'
};

// Helper function to get color based on connection quality
export const getConnectionQualityColor = (quality) => {
  switch(quality) {
    case CONNECTION_QUALITY.STRONG:
      return '#119B05'; // Green
    case CONNECTION_QUALITY.WEAK:
      return '#FFA500'; // Orange
    case CONNECTION_QUALITY.VERY_WEAK:
      return '#FF6B35'; // Red-Orange
    case CONNECTION_QUALITY.OFFLINE:
      return '#f44336'; // Red
    default:
      return '#757575'; // Grey
  }
};

// Helper function to get quality display text
export const getConnectionQualityText = (quality, responseTime = null) => {
  const timeText = responseTime ? ` (${responseTime}ms)` : '';
  switch(quality) {
    case CONNECTION_QUALITY.STRONG:
      return `Strong${timeText}`;
    case CONNECTION_QUALITY.WEAK:
      return `Weak${timeText}`;
    case CONNECTION_QUALITY.VERY_WEAK:
      return `Very Weak${timeText}`;
    case CONNECTION_QUALITY.OFFLINE:
      return 'Offline';
    default:
      return 'Unknown';
  }
};

// Hook to monitor browser online/offline status
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Hook to test actual internet connectivity
export const useInternetConnection = () => {
  const [isConnected, setIsConnected] = useState(true);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch('https://www.google.com/favicon.ico', { 
          mode: 'no-cors',
          cache: 'no-cache',
          signal: AbortSignal.timeout(5000) // 5 second timeout
        });
        setIsConnected(true);
      } catch (error) {
        setIsConnected(false);
      }
    };

    const intervalId = setInterval(checkConnection, 10000); // Check every 10 seconds
    checkConnection(); // Initial check

    return () => clearInterval(intervalId);
  }, []);

  return isConnected;
};

// Hook to test API connectivity using RTK Query with response time monitoring
export const useApiConnectionViaRTK = () => {
  const [lastChecked, setLastChecked] = useState(new Date());
  const [responseTime, setResponseTime] = useState(null);
  const [avgResponseTime, setAvgResponseTime] = useState(null);
  const [connectionQuality, setConnectionQuality] = useState(CONNECTION_QUALITY.STRONG);
  const responseTimes = useRef([]);
  const startTimeRef = useRef(null);
  
  // Use existing RTK Query hook with polling for connection testing
  const {
    isError,
    isSuccess,
    isLoading,
    error,
    refetch: originalRefetch
  } = useGetJobCardStateQuery(undefined, {
    pollingInterval: 15000, // Poll every 15 seconds
    skipPollingIfUnfocused: true, // Don't poll when tab is not active
    refetchOnReconnect: true, // Refetch when network reconnects
    refetchOnFocus: true // Refetch when tab gets focus
  });

  // Enhanced refetch function with timing
  const refetch = () => {
    startTimeRef.current = performance.now();
    return originalRefetch();
  };

  // Calculate connection quality based on response time
  const calculateConnectionQuality = (responseTime) => {
    if (responseTime === null) return CONNECTION_QUALITY.OFFLINE;
    if (responseTime < CONNECTION_THRESHOLDS.STRONG) return CONNECTION_QUALITY.STRONG;
    if (responseTime < CONNECTION_THRESHOLDS.WEAK) return CONNECTION_QUALITY.WEAK;
    return CONNECTION_QUALITY.VERY_WEAK;
  };

  // Calculate rolling average of last 5 response times
  const updateResponseTimes = (newTime) => {
    responseTimes.current = [...responseTimes.current, newTime].slice(-5); // Keep only last 5
    const average = responseTimes.current.reduce((sum, time) => sum + time, 0) / responseTimes.current.length;
    setAvgResponseTime(Math.round(average));
  };

  // Monitor query completion and measure response time
  useEffect(() => {
    if ((isSuccess || isError) && startTimeRef.current !== null) {
      const endTime = performance.now();
      const currentResponseTime = Math.round(endTime - startTimeRef.current);
      
      setResponseTime(currentResponseTime);
      setLastChecked(new Date());
      startTimeRef.current = null;
      
      // Only update response times if API actually responded (not network error)
      if (isSuccess || (isError && error?.status)) {
        updateResponseTimes(currentResponseTime);
        setConnectionQuality(calculateConnectionQuality(currentResponseTime));
      } else {
        // Network error - treat as offline
        setConnectionQuality(CONNECTION_QUALITY.OFFLINE);
      }
    }
  }, [isSuccess, isError, error]);

  // Start timing when loading begins
  useEffect(() => {
    if (isLoading && startTimeRef.current === null) {
      startTimeRef.current = performance.now();
    }
  }, [isLoading]);

  // Determine if API is connected
  // API is considered connected if:
  // 1. Query is successful, OR
  // 2. Query fails with 401 (unauthorized) - means API is responding but user needs auth
  const isApiConnected = isSuccess || (isError && error?.status === 401);
  
  // Determine if connection is weak
  const isWeakConnection = connectionQuality === CONNECTION_QUALITY.WEAK || 
                           connectionQuality === CONNECTION_QUALITY.VERY_WEAK;

  return {
    isApiConnected,
    isWeakConnection,
    connectionQuality,
    responseTime,
    avgResponseTime,
    lastChecked,
    isLoading,
    refetch,
    error: isError ? error : null
  };
};

// Comprehensive connection test hook combining all connection types
export const useComprehensiveConnectionTest = () => {
  const networkStatus = useNetworkStatus();
  const internetConnection = useInternetConnection();
  const { 
    isApiConnected, 
    isWeakConnection,
    connectionQuality,
    responseTime,
    avgResponseTime,
    lastChecked, 
    isLoading, 
    refetch, 
    error 
  } = useApiConnectionViaRTK();

  const isFullyConnected = networkStatus && internetConnection && isApiConnected;

  // Helper function to get API status text with timing info
  const getApiStatusText = () => {
    if (!isApiConnected) return 'API Unavailable';
    if (responseTime === null) return 'API Responding';
    
    const timeText = avgResponseTime ? ` (avg: ${avgResponseTime}ms)` : ` (${responseTime}ms)`;
    switch(connectionQuality) {
      case CONNECTION_QUALITY.STRONG:
        return `API Fast${timeText}`;
      case CONNECTION_QUALITY.WEAK:
        return `API Slow${timeText}`;
      case CONNECTION_QUALITY.VERY_WEAK:
        return `API Very Slow${timeText}`;
      default:
        return `API Responding${timeText}`;
    }
  };

  return {
    isFullyConnected,
    networkStatus,
    internetConnection,
    isApiConnected,
    isWeakConnection,
    connectionQuality,
    responseTime,
    avgResponseTime,
    lastChecked,
    isLoading,
    refetch,
    error,
    connectionDetails: {
      network: networkStatus ? 'Online' : 'Offline',
      internet: internetConnection ? 'Connected' : 'No Internet',
      api: getApiStatusText()
    }
  };
};

// Enhanced hook for individual installer pages with connection quality
export const useConnectionMonitor = () => {
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [weakWarningDismissed, setWeakWarningDismissed] = useState(false);
  
  // Get comprehensive connection info including response times
  const { 
    isFullyConnected,
    networkStatus,
    internetConnection,
    isApiConnected,
    isWeakConnection,
    connectionQuality,
    responseTime,
    avgResponseTime,
    connectionDetails: fullConnectionDetails,
    refetch
  } = useComprehensiveConnectionTest();

  // Only consider network and internet for basic connectivity
  const isConnected = networkStatus && internetConnection;
  
  // Show alert when connection is lost (and not manually dismissed)
  const showAlert = !isConnected && !alertDismissed;
  
  // Show weak connection warning when API is slow (and not dismissed)
  const showWeakWarning = isFullyConnected && isWeakConnection && !weakWarningDismissed;

  // Reset dismissals when connection changes
  useEffect(() => {
    if (isConnected) {
      setAlertDismissed(false); // Reset when connection restored
    }
    if (!isWeakConnection) {
      setWeakWarningDismissed(false); // Reset when connection improves
    }
  }, [isConnected, isWeakConnection]);

  const handleRetry = () => {
    // Clear dismissals and refetch API data
    setAlertDismissed(false);
    setWeakWarningDismissed(false);
    if (refetch) refetch();
  };

  const handleDismiss = () => {
    setAlertDismissed(true);
  };

  const handleWeakWarningDismiss = () => {
    setWeakWarningDismissed(true);
  };

  const connectionDetails = {
    networkStatus,
    internetConnection,
    isApiConnected,
    network: networkStatus ? 'Online' : 'Offline',
    internet: internetConnection ? 'Connected' : 'No Internet',
    api: fullConnectionDetails.api
  };

  return {
    isConnected,
    isFullyConnected,
    isWeakConnection,
    connectionQuality,
    responseTime,
    avgResponseTime,
    showAlert,
    showWeakWarning,
    handleRetry,
    handleDismiss,
    handleWeakWarningDismiss,
    connectionDetails
  };
};